import { useState } from "react";
import { ActionModal, FIELD_LABEL_CLASS, INPUT_CLASS } from "./ActionModal";
import { CONDITION_ACCEPT_DISCLOSURE } from "../../vocabulary";
import type { ConditionReasonCategory, ConditionScopeType } from "../../types";

/**
 * Accept a condition (§5.3 per-problem overflow / §5.2 rule scope): this
 * signal is deliberately tolerated on this hotel from now on.
 *
 * Both scopes come through here. A **problem** scope disposes of one Validator
 * finding class; a **rule** scope disposes of one of the five daily health
 * rules — the Rule 3 relief class, the only accepted condition the fleet
 * actually carries today. The scope decides what the copy promises, because
 * the two are not symmetrical: a problem-scoped condition is judged per
 * Validator run, a rule-scoped one per daily evaluation, and the daily half is
 * what an operator watching a red rule row is actually waiting on.
 *
 * **No category picker (2026-08-13).** Accepting is one decision — on or off —
 * and asking an operator to first classify it `commercial` vs `structural` put
 * a taxonomy question in front of the act. The distinction still exists in the
 * store, so it is DERIVED from the one thing that actually differs between the
 * two: whether there is something that would make us look again. Name an exit
 * condition and the record is structural (the backend requires the prose and
 * keeps it verbatim); leave it blank and it is commercial (the backend refuses
 * an exit condition on those). Same rows, same API, one less question.
 */
export function AcceptConditionModal({
  scopeType,
  scopeId,
  scopeLabel,
  onSubmit,
  onClose,
  pending,
}: {
  scopeType: ConditionScopeType;
  scopeId: string;
  scopeLabel: string;
  onSubmit: (body: {
    scopeType: ConditionScopeType;
    scopeId: string;
    reasonCategory: ConditionReasonCategory;
    reason: string;
    exitCondition?: string;
  }) => void;
  onClose: () => void;
  pending?: boolean;
}) {
  const [reason, setReason] = useState("");
  const [exitCondition, setExitCondition] = useState("");

  const exit = exitCondition.trim();
  const canSubmit = !!reason.trim();

  return (
    <ActionModal
      title={`Accept as condition — ${scopeLabel}`}
      submitLabel="Accept condition"
      onSubmit={() =>
        onSubmit({
          scopeType,
          scopeId,
          // Derived, never asked: an exit condition is what makes a
          // disposition structural, and the backend refuses one on a
          // commercial record.
          reasonCategory: exit ? "structural" : "commercial",
          reason: reason.trim(),
          exitCondition: exit || undefined,
        })
      }
      onClose={onClose}
      pending={pending}
      disabled={!canSubmit}
    >
      <p className="text-sm text-gray-500">
        {scopeType === "rule" ? (
          <>
            Every future evaluation of this rule on this hotel auto-closes
            instead of opening an incident. The rule keeps being evaluated and
            the record keeps saying <em>fail</em> — the disposition is on the
            signal, not on the measurement.
          </>
        ) : (
          <>
            Every future Validator finding for this problem on this hotel
            auto-closes instead of opening an incident. The check keeps running
            and the row keeps saying <em>finding</em> — the disposition is on
            the signal, not on the measurement.
          </>
        )}
      </p>
      <div>
        <label className={FIELD_LABEL_CLASS}>Why we live with it (required)</label>
        <textarea
          className={`${INPUT_CLASS} min-h-[66px]`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. the property books almost entirely by phone, so the paid-session ratio can never clear the rule"
          autoFocus
        />
      </div>
      <div>
        <label className={FIELD_LABEL_CLASS}>
          What would make us look again (optional — recorded verbatim)
        </label>
        <textarea
          className={`${INPUT_CLASS} min-h-[50px]`}
          value={exitCondition}
          onChange={(e) => setExitCondition(e.target.value)}
          placeholder="e.g. if the hotel moves to a booking engine that emits purchase events"
        />
      </div>
      <p className="text-xs text-gray-400">{CONDITION_ACCEPT_DISCLOSURE}</p>
    </ActionModal>
  );
}
